import { createTheme, type Theme } from '@mui/material/styles';
import { dark, light, staticTokens, type ColorMode } from './tokens';

/**
 * Tema MUI derivado dos tokens Zarya (`docs/design/zarya-tokens.html`).
 *
 * Mapeamento (decidido aqui; ver relatório da task T011):
 *   --primary          → palette.primary.main
 *   --primary-active   → palette.primary.dark        (é o estado :hover dos botões)
 *   --primary-light    → palette.primary.light
 *   --secondary-main   → palette.secondary.main
 *   --surface          → palette.background.paper    (superfície de campo/menu)
 *   --default          → palette.background.default  (fundo da área de conteúdo)
 *   --fg/--fg-3/--fg-4 → text.primary / text.secondary / text.disabled
 *   --crit-*           → palette.error/warning/success (severidades do produto)
 *   --r-btn/--r-paper  → shape.borderRadius e overrides por componente
 *   --shadow           → shadows[1] (o design tem uma sombra só; os níveis
 *                        superiores do MUI permanecem os padrão)
 */
export function createAimTheme(mode: ColorMode = 'light'): Theme {
  const t = mode === 'dark' ? dark : light;
  const radius = (v: string) => Number.parseInt(v, 10);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: t.primary,
        dark: t['primary-active'],
        light: t['primary-light'],
        contrastText: t['primary-contrast'],
      },
      secondary: { main: t['secondary-main'], contrastText: t['primary-contrast'] },
      error: { main: t['crit-critical'], light: t['danger-light'], dark: t['danger-dark'] },
      warning: { main: t['crit-high'], light: t['warning-light'], dark: t['warning-dark'] },
      success: { main: t['crit-minimum'], light: t['success-light'], dark: t['success-dark'] },
      info: { main: t.primary, light: t['info-light'], dark: t['info-dark'] },
      background: { default: t.default, paper: t.surface },
      text: { primary: t.fg, secondary: t['fg-3'], disabled: t['fg-4'] },
      divider: t.divider,
    },
    typography: {
      fontFamily: staticTokens.font,
      fontSize: 14,
      htmlFontSize: 16,
      // Escala tirada dos componentes: .ph h1 (30/40), .card-title (17/24),
      // .mh h3 (19), .dh h2 (20/26), corpo 14/20, .tiny 13/19, .fhelp 12/17.
      h1: { fontSize: 30, lineHeight: '40px', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: 20, lineHeight: '26px', fontWeight: 700 },
      h3: { fontSize: 19, lineHeight: '26px', fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontSize: 16, lineHeight: '22px', fontWeight: 600 },
      h5: { fontSize: 15, lineHeight: '20px', fontWeight: 600 },
      h6: { fontSize: 17, lineHeight: '24px', fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle1: { fontSize: 15, lineHeight: '22px' },
      subtitle2: { fontSize: 13, lineHeight: '18px', fontWeight: 600 },
      body1: { fontSize: 14, lineHeight: '20px' },
      body2: { fontSize: 13, lineHeight: '19px' },
      caption: { fontSize: 12, lineHeight: '17px' },
      overline: {
        fontSize: 11,
        lineHeight: '12px',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
      button: { fontSize: 14, fontWeight: 600, textTransform: 'none' },
    },
    shape: { borderRadius: radius(staticTokens['r-btn']) },
    spacing: 4,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.default,
            color: t.fg,
            fontFamily: staticTokens.font,
            fontSize: 14,
            lineHeight: '20px',
          },
          ':focus-visible': { outline: `2px solid ${t.primary}`, outlineOffset: 2 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius(staticTokens['r-btn']), height: 40, padding: '0 20px' },
          sizeSmall: {
            height: 32,
            padding: '0 12px',
            fontSize: 13,
            borderRadius: radius(staticTokens['r-btn-sm']),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: radius(staticTokens['r-paper']) },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: radius(staticTokens['r-dialog']) } },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: radius(staticTokens['r-input']) } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            // O balão usa `--header`, que é escuro nos DOIS temas — então a tinta
            // é `--on-header`, não `--primary-contrast`. Aquele é o contraste do
            // `--primary` e no escuro vira tinta escura (#062220): dava 1,12:1,
            // e é aqui que mora o texto explicativo de 12 telas (FR-132).
            backgroundColor: t.header,
            color: t['on-header'],
            fontSize: 12,
            lineHeight: '17px',
            padding: '8px 10px',
            borderRadius: radius(staticTokens['r-btn-sm']),
            maxWidth: 320,
          },
          arrow: { color: t.header },
        },
      },
    },
  });

  // O design tem uma sombra só (`--shadow`); ela vira o nível 1 do MUI.
  theme.shadows[1] = t.shadow;
  return theme;
}

/** Fonte monoespaçada dos identificadores técnicos (FR-133). */
export const monoFontFamily = staticTokens.mono;
